import { ApiResult } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { CloudKeysDto } from 'src/Libs/installation-rights-management/dto'
import { CloudKeyModel } from 'src/Libs/installation-rights-management/models'
import { cloudKeysDtoToModelTransformer } from 'src/Libs/installation-rights-management/transformer'
import { FetchProps } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { StatusCodes } from '@e3dc-react/shell/Libs/http-status-codes'
import { CloudKeyScopeFilters } from '../types'
import { handleFetchError } from 'src/Libs/api/utils'

/**
 * Returns all the available CloudKeys for a single Installation
 *
 * @param installationId
 * @param authFetch
 * @param filter // allows to filter by scope
 */
const findAll = async (
  installationId: number,
  authFetch: AuthFetchWithResponse,
  filter?: CloudKeyScopeFilters
): Promise<ApiResult<CloudKeyModel[]>> => {
  const fetchProps = {
    path: `installations/${installationId}/cloud-keys`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    method: 'get',
    payload: { filter: filter },
  } as FetchProps

  try {
    const response = await authFetch<void>(fetchProps)

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult([], false)
    }
    if (response.status === StatusCodes.OK) {
      const models = cloudKeysDtoToModelTransformer(response.data as unknown as CloudKeysDto)
      return new ApiResult(models, false)
    }
    return new ApiResult<CloudKeyModel[]>(null, true)
  } catch (error) {
    return handleFetchError<CloudKeyModel[]>(error)
  }
}

/**
 * Request to create a new CloudKey for a single Installation
 *
 * @param installationId
 * @param description
 * @param authFetch
 */
const createNew = async (installationId: number, description: string, authFetch: AuthFetchWithResponse): Promise<ApiResult<number>> => {
  const fetchProps = {
    path: `installations/${installationId}/cloud-keys`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    method: 'post',
    payload: { description },
  } as FetchProps

  const response = await authFetch<void>(fetchProps)

  if (response.status === StatusCodes.CREATED) {
    return new ApiResult(Number(response.data), false)
  }
  return new ApiResult<number>(null, true)
}

/**
 * Removes an existing CloudKey from an Installation.
 *
 * @param installationId
 * @param cloudKeyId
 * @param authFetch
 */
const removeOne = async (installationId: number, cloudKeyId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<void>> => {
  const fetchProps = {
    path: `installations/${installationId}/cloud-keys/${cloudKeyId}`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    method: 'delete',
  } as FetchProps

  const response = await authFetch<void>(fetchProps)

  if (response.status === StatusCodes.CREATED) {
    return new ApiResult(null, false)
  }
  return new ApiResult(null, true)
}

/**
 * Updating the meta information of an CloudKey to an Installation.
 *
 * @param installationId
 * @param cloudKeyId
 * @param description
 * @param authFetch
 */
const updateOne = async (
  installationId: number,
  cloudKeyId: number,
  description: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<void>> => {
  const fetchProps = {
    path: `installations/${installationId}/cloud-keys/${cloudKeyId}`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    method: 'put',
    payload: { description },
  } as FetchProps

  const response = await authFetch<void>(fetchProps)

  if (response.status === StatusCodes.CREATED) {
    return new ApiResult(null, false)
  }
  return new ApiResult(null, true)
}

const allApis = { findAll, createNew, removeOne, updateOne }

export default allApis
